import { NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  Component,
  ContentChild,
  Input,
  OnDestroy,
  TemplateRef
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Ripple } from 'primeng/ripple';
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { ToastModule } from 'primeng/toast';
import { ChipsModule } from 'primeng/chips';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { AuthenticationService } from '@adlatus-gui/domain/authentication';
import { getNameInitials } from '../../util/name.util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ScrollTopModule } from 'primeng/scrolltop';
import { filter, Subscription } from 'rxjs';


@Component({
  selector: 'lib-main-layout',
  standalone: true,
  providers: [AuthenticationService],
  imports: [
    RouterOutlet,
    ButtonModule,
    Ripple,
    StyleClassModule,
    BadgeModule,
    RouterLink,
    AvatarModule,
    NgTemplateOutlet,
    ChipsModule,
    MenuModule,
    OverlayPanelModule,
    TooltipModule,
    ToastModule,
    TranslatePipe,
    ScrollTopModule,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnDestroy {
  protected readonly getNameInitials = getNameInitials;
  private readonly darkTheme = 'aura-dark-noir.css';
  private readonly lightTheme = 'aura-light-noir.css';
  private readonly themeTransitionClass = 'theme-switching';
  @ContentChild('navigationTemplate', { static: true })
  navigationTemplate!: TemplateRef<never>;
  @ContentChild('userTemplate', { static: true })
  userTemplate!: TemplateRef<never>;

  goBackToButtonUrl = false;
  isSidebarCollapsed = true;
  isLightRoute = false;
  private themeTransitionTimeoutId?: number;
  private routeSubscription?: Subscription;

  userMenuItems: MenuItem[];
  @Input() systemName!: string;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private translateService: TranslateService
  ) {
    this.isLightRoute = this.isLightRouteUrl(this.router.url);

    const storedSidebarState = localStorage.getItem('sidebar_collapsed');
    if (storedSidebarState !== null) {
      this.isSidebarCollapsed = storedSidebarState === 'true';
    }

    this.userMenuItems = this.buildUserMenuItems();

    this.translateService.onLangChange.subscribe(() => {
      this.userMenuItems = this.buildUserMenuItems();
    });

    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.isLightRoute = this.isLightRouteUrl((event as NavigationEnd).urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    if (this.themeTransitionTimeoutId) {
      window.clearTimeout(this.themeTransitionTimeoutId);
    }
    document.documentElement.classList.remove(this.themeTransitionClass);
  }

  private buildUserMenuItems(): MenuItem[] {
    return [
      {
        label: this.translateService.instant('my_account'),
        icon: 'pi pi-user',
        command: () => this.information(),
      },
      {
        label: this.translateService.instant('logout'),
        icon: 'pi pi-sign-out',
        styleClass: 'text-red-200',
        iconClass: 'text-red-500',
        command: () => this.logout(),
      },
      {
        separator: true,
      },
      {
        label: this.translateService.instant('lang_en'),
        icon: 'flag flag-gb',
        command: () => this.setLanguage('en'),
      },
      {
        label: this.translateService.instant('lang_de'),
        icon: 'flag flag-de',
        command: () => this.setLanguage('de'),
      },
    ];
  }

  private setLanguage(lang: string) {
    localStorage.setItem('selectedLanguage', lang);
    this.translateService.use(lang);
  }

  get fullName() {
    return localStorage.getItem('fullName');
  }

  get title() {
    return document.title;
  }

  get isDarkTheme() {
    const themeHref = this.getCurrentThemeHref();
    return themeHref.includes(this.darkTheme);
  }

  get headerLogoSrc() {
    return this.isDarkTheme
      ? 'images/pp_logo_green_light@1x.png'
      : 'images/pp_logo_green_dark@1x.png';
  }

  logout() {
    this.authenticationService
      .logout(window.location.href)
      .then(() => console.info('successfully logged out'));
  }

  async information() {
    await this.router.navigate(['user']);
  }

  toggleTheme() {
    const nextTheme = this.isDarkTheme ? this.lightTheme : this.darkTheme;
    this.applyTheme(nextTheme);
  }

  private applyTheme(theme: string) {
    const themeLink = document.getElementById('app-theme') as HTMLLinkElement | null;
    if (!themeLink) {
      return;
    }

    document.documentElement.classList.add(this.themeTransitionClass);
    if (this.themeTransitionTimeoutId) {
      window.clearTimeout(this.themeTransitionTimeoutId);
    }

    requestAnimationFrame(() => {
      themeLink.href = theme;
    });

    this.themeTransitionTimeoutId = window.setTimeout(() => {
      document.documentElement.classList.remove(this.themeTransitionClass);
      this.themeTransitionTimeoutId = undefined;
    }, 320);

    localStorage.setItem('app-theme', theme);
  }

  private getCurrentThemeHref(): string {
    const themeLink = document.getElementById('app-theme') as HTMLLinkElement | null;
    return themeLink?.getAttribute('href') ?? '';
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    localStorage.setItem('sidebar_collapsed', String(this.isSidebarCollapsed));
  }

  private isLightRouteUrl(url: string): boolean {
    return url.toLowerCase().includes('/light');
  }
}
